/**
 * T16001 · Structural-equality memo for `perEntitySubjects(args)`.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §431 — "perEntitySubjects is a pure function of args by value; the SDK
 * calls it through a structural-equality memo keyed on `JSON.stringify(args)`
 * at the call site."
 *
 * WHY THIS MATTERS
 * ----------------
 * T15001's earlier `createHook` body called `realtime.perEntitySubjects(args)`
 * directly every render. Downstream the registry collapses the array to a
 * `'|'.join(...)` so subject sets that are value-equal don't churn — BUT the
 * intermediate array allocation runs on every render of every viewport-gated
 * row in a virtualized list. At 1000 rows × 60 fps that's 60k array
 * allocations per second on a scroll, all immediately collected. Trivial fix:
 * a single-slot per-hook ref keyed on `JSON.stringify(args)` returns the SAME
 * array reference when args are value-equal.
 *
 * HASH REUSE WITH T2004
 * ---------------------
 * T2004 will ship a dev-only purity assertion on `perEntitySubjects` (asserts
 * that two calls with `JSON.stringify`-equal args produce reference-stable
 * arrays). Both code paths call `JSON.stringify(args)` to key the check;
 * doing it twice per render is wasteful. The memo returns the computed
 * `argsHash` so T2004 can read the same value without recomputing.
 *
 * NO LRU
 * ------
 * The memo intentionally caches ONE slot. The spec §431 contract is
 * structural-equality on the LAST call, not LRU; a thrashing args sequence
 * `A → B → A → B → …` MUST re-invoke the user fn each time. This matches the
 * worst-case allocation cost of doing it directly, while still saving on the
 * common case of identical-by-value rerenders.
 */
import { useRef } from 'react';
/**
 * Pure structural-equality memo step used by `useStableEntitySubjects`.
 *
 * Split out so unit tests can drive the memo logic directly without a
 * React render context (this package ships no `@testing-library/react`).
 * Callers pass mutable slot containers; the function mutates them
 * in-place when the args hash changes.
 *
 * @returns The (now-current) subjects array reference + the computed
 *          argsHash. Callers expose the hash so T2004's purity check can
 *          consume the same string — no double `JSON.stringify` per render.
 */
export function stableEntitySubjectsStep(fn, args, hashSlot, subjectsSlot) {
  const argsHash = JSON.stringify(args);
  if (hashSlot.current !== argsHash) {
    hashSlot.current = argsHash;
    subjectsSlot.current = fn(args);
  }
  return {
    subjects: subjectsSlot.current,
    argsHash
  };
}
/**
 * Reference-stable wrapper around `perEntitySubjects(args)`. Returns the
 * SAME array reference on rerenders whose `args` value is structurally
 * equal to the previous call (compared via `JSON.stringify`).
 *
 * @param fn    The user-provided `perEntitySubjects` function. Must be pure
 *              w.r.t. args; T2004's dev-only assertion enforces this.
 * @param args  The hook's args object — passed by value, hashed.
 * @returns     Both the (memoized) subjects array and the computed argsHash
 *              so T2004 can consume the same string.
 */
export function useStableEntitySubjects(fn, args) {
  'use no memo';

  const lastHashRef = useRef(null);
  const lastSubjectsRef = useRef([]);
  return stableEntitySubjectsStep(fn, args, lastHashRef, lastSubjectsRef);
}